/**
 * Project 4: 디자인 템플릿 서비스
 */

import axios from 'axios';

const apiClient = axios.create({
  baseURL: '/api',
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
});

export interface DesignTemplate {
  template_id: string;
  template_name: string;
  description: string;
  category: 'layout' | 'theme' | 'color' | 'typography' | 'custom';
  tags: string[];
  css_class: string;
  css_code: string;
  is_premium: boolean;
  usage_count: number;
  created_at: string;
}

export interface TemplatesResponse {
  success: boolean;
  templates: DesignTemplate[];
}

class DesignTemplateService {
  /**
   * 모든 템플릿 조회
   */
  async getAllTemplates(): Promise<DesignTemplate[]> {
    try {
      const response = await apiClient.get<TemplatesResponse>('/v2/designs/templates.php');
      if (response.data.success) {
        return response.data.templates;
      }
      throw new Error('템플릿 조회 실패');
    } catch (error: any) {
      console.error('[DesignTemplateService] getAllTemplates error:', error);
      throw new Error(error.response?.data?.error || '템플릿을 불러올 수 없습니다.');
    }
  }

  /**
   * 카테고리별 템플릿 조회
   */
  async getTemplatesByCategory(category: string): Promise<DesignTemplate[]> {
    try {
      const response = await apiClient.get<TemplatesResponse>('/v2/designs/templates.php', {
        params: { category }
      });
      if (response.data.success) {
        return response.data.templates;
      }
      throw new Error('템플릿 조회 실패');
    } catch (error: any) {
      console.error('[DesignTemplateService] getTemplatesByCategory error:', error);
      throw new Error(error.response?.data?.error || '템플릿을 불러올 수 없습니다.');
    }
  }

  /**
   * 템플릿 CSS 적용
   */
  applyTemplateCSS(template: DesignTemplate): void {
    const styleId = `template-${template.template_id}`;
    let styleElement = document.getElementById(styleId);
    
    if (!styleElement) {
      styleElement = document.createElement('style');
      styleElement.id = styleId;
      document.head.appendChild(styleElement);
    }
    
    styleElement.textContent = template.css_code;
  }

  /**
   * 템플릿 CSS 제거
   */
  removeTemplateCSS(templateId: string): void {
    const styleId = `template-${templateId}`;
    const styleElement = document.getElementById(styleId);
    if (styleElement) {
      styleElement.remove();
    }
  }

  /**
   * 콘텐츠에 템플릿 클래스 적용
   */
  applyTemplateClass(element: HTMLElement, template: DesignTemplate): void {
    // 기존 템플릿 클래스 제거
    const existingClasses = Array.from(element.classList).filter(cls => 
      cls.startsWith('template-') || cls.startsWith('classic-') || 
      cls.startsWith('modern-') || cls.startsWith('cyberpunk-') || 
      cls.startsWith('pastel-')
    );
    existingClasses.forEach(cls => element.classList.remove(cls));
    
    // 새 템플릿 클래스 적용
    element.classList.add(template.css_class);
  }

  /**
   * 템플릿 사용 횟수 증가
   */
  async incrementUsage(templateId: string): Promise<void> {
    try {
      await apiClient.post('/v2/designs/use.php', {
        template_id: templateId
      });
    } catch (error: any) {
      console.error('[DesignTemplateService] incrementUsage error:', error);
      // 실패해도 계속 진행 (비중요)
    }
  }
}

export const designTemplateService = new DesignTemplateService();

